import { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'

// AI-OWNERS-1 — the owner dropdown used by the AI Register and Risk actions.
// While no owner is active (or the list cannot be read) it is today's text
// box, so nothing changes until a super admin adds the first owner.

export interface AiOwner {
  id: string
  name: string
  email: string
  title: string
  active: boolean
  updated_at?: string
  updated_by?: string
}

export const OWNERS_CHANGED = 'ai-owners-changed'

/** Active owners. Re-fetches on the `ai-owners-changed` window event; any error -> []. */
export function useAiOwners(): AiOwner[] {
  const [owners, setOwners] = useState<AiOwner[]>([])
  useEffect(() => {
    let alive = true
    const load = () => {
      Promise.resolve()
        .then(() => apiClient.get<{ owners?: AiOwner[] }>('/v1/admin/ai-owners'))
        .then(res => {
          const list = res?.data?.owners
          if (alive) setOwners(Array.isArray(list) ? list.filter(o => o.active) : [])
        })
        .catch(() => { if (alive) setOwners([]) })
    }
    load()
    window.addEventListener(OWNERS_CHANGED, load)
    return () => { alive = false; window.removeEventListener(OWNERS_CHANGED, load) }
  }, [])
  return owners
}

interface PickerProps {
  value: string
  onChange: (email: string) => void
  testId: string
  allowBlank?: boolean
  owners: AiOwner[]
}

export function OwnerPicker({ value, onChange, testId, allowBlank, owners }: PickerProps) {
  if (owners.length === 0) {
    return (
      <input type="text" data-testid={testId} placeholder="email" className="w-48 rounded border px-2 py-1"
        value={value} onChange={e => onChange(e.target.value)} />
    )
  }
  const listed = owners.some(o => o.email.toLowerCase() === value.trim().toLowerCase())
  return (
    <select data-testid={testId} className="w-48 rounded border px-2 py-1" value={value}
      onChange={e => onChange(e.target.value)}>
      {allowBlank && <option value="">Not set</option>}
      {!allowBlank && !value && <option value="">Choose an owner</option>}
      {owners.map(o => <option key={o.id} value={o.email}>{o.name} - {o.email}</option>)}
      {value.trim() !== '' && !listed && <option value={value}>{value} (not on list)</option>}
    </select>
  )
}
